import { queryOptions, useQuery } from '@tanstack/react-query';

import { CostReportAllocationGroupTimedSummaryItem } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ALLOCATION_GROUPS_TIMED_SUMMARIES_QUERY_KEY =
  'allocation-groups-timed-summaries';

const getAllocationGroupsTimedSummariesQueryKey = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  groupId,
  includeIdleCosts,
}: Partial<UseAllocationGroupsTimedSummariesQueryProps> = {}) =>
  [
    ALLOCATION_GROUPS_TIMED_SUMMARIES_QUERY_KEY,
    ...(startTime !== undefined
      ? [
          startTime,
          endTime,
          useListingPrices,
          clusterIds,
          groupId,
          includeIdleCosts,
        ]
      : []),
  ] as const;

type UseAllocationGroupsTimedSummariesQuery = {
  data?: CostReportAllocationGroupTimedSummaryItem[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
};

type UseAllocationGroupsTimedSummariesQueryProps = {
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  clusterIds?: string[];
  groupId?: string;
  includeIdleCosts?: boolean;
  enabled?: boolean;
};

const getAllocationGroupsTimedSummariesQueryOptions = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  groupId,
  includeIdleCosts = false,
  enabled = true,
}: UseAllocationGroupsTimedSummariesQueryProps) =>
  queryOptions({
    queryKey: getAllocationGroupsTimedSummariesQueryKey({
      startTime,
      endTime,
      useListingPrices,
      clusterIds,
      groupId,
      includeIdleCosts,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getAllocationGroupTimedSummaries({
          clusterIds,
          startTime,
          endTime,
          groupId,
          useListingPrices,
          includeIdleResourceCosts: includeIdleCosts,
        });
      return data.items;
    },
    enabled: Boolean(enabled && startTime && endTime),
    refetchOnWindowFocus: false,
  });

export const useAllocationGroupsTimedSummariesQuery = (
  props: UseAllocationGroupsTimedSummariesQueryProps
): UseAllocationGroupsTimedSummariesQuery => {
  const { isLoading, data, error, refetch } = useQuery(
    getAllocationGroupsTimedSummariesQueryOptions(props)
  );

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
